import { useState, useCallback } from 'react';
import { apiManager } from '@/services/implementations/APIManager';
import { toast } from 'sonner';
import { usePaymentFlow, type PaymentProvider } from '@/hooks/usePaymentFlow';
import type { SubscriptionPlan, TokenPack, PaymentOrder } from '@/types/payment';

export type PurchaseType = 'subscription' | 'token';

// Hook for using the ViewModel in React components
export function usePurchaseViewModel() {
  // State
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [tokenPacks, setTokenPacks] = useState<TokenPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Payment method selection state
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] = useState(false);
  const [currentPurchaseType, setCurrentPurchaseType] = useState<PurchaseType | null>(null);
  const [currentItemId, setCurrentItemId] = useState('');
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState<PaymentProvider | null>(null);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState<PaymentProvider | null>(null);

  // Use shared payment flow hook
  const paymentFlow = usePaymentFlow({
    onPaymentSuccess: () => {
      // Refresh the page or reload data
      window.location.reload();
    },
  });
  
  // Business Logic Methods
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [plansResponse, packsResponse] = await Promise.all([
        apiManager.getSubscriptionPlans(),
        apiManager.getTokenPacks()
      ]);
      
      setSubscriptionPlans(plansResponse.data);
      setTokenPacks(packsResponse.data);
    } catch (error) {
      console.error('Failed to load purchase data:', error);
      setError('Failed to load purchase data');
      toast.error('Failed to load purchase data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const initiatePurchase = useCallback((type: PurchaseType, itemId: string) => {
    setCurrentPurchaseType(type);
    setCurrentItemId(itemId);
    setIsPaymentMethodModalVisible(true);
  }, []);
  
  const selectPaymentMethod = useCallback(async (provider: PaymentProvider) => {
    setSelectedPaymentProvider(provider);
    setLoadingPaymentMethod(provider);

    if (!currentPurchaseType || !currentItemId) {
      toast.error('Invalid purchase information');
      setLoadingPaymentMethod(null);
      return;
    }

    try {
      let orderResponse: PaymentOrder;

      if (currentPurchaseType === 'subscription') {
        if (provider === 'alipay') {
          orderResponse = await apiManager.createSubscriptionAlipayOrder(currentItemId);
        } else {
          orderResponse = await apiManager.createSubscriptionPolarOrder(currentItemId);
        }
      } else {
        if (provider === 'alipay') {
          orderResponse = await apiManager.createTokenPackAlipayOrder(currentItemId);
        } else {
          orderResponse = await apiManager.createTokenPackPolarOrder(currentItemId);
        }
      }

      if (orderResponse.success && orderResponse.orderId && orderResponse.paymentUrl) {
        // Start the payment flow using the shared hook
        paymentFlow.startPaymentFlow(provider, {
          orderId: orderResponse.orderId,
          paymentUrl: orderResponse.paymentUrl
        });

        // Clear loading state
        setLoadingPaymentMethod(null);

        // Keep payment method modal open for Polar (non-dismissible)
        if (provider === 'alipay') {
          setIsPaymentMethodModalVisible(false);
        }
      } else {
        toast.error('Failed to create payment order');
        setLoadingPaymentMethod(null);
        resetPayment();
      }
    } catch (error) {
      console.error('Payment creation failed:', error);
      toast.error('Payment creation failed');
      setLoadingPaymentMethod(null);
      resetPayment();
    }
  }, [currentPurchaseType, currentItemId, paymentFlow]);
  
  const resetPayment = useCallback(() => {
    setIsPaymentMethodModalVisible(false);
    setCurrentPurchaseType(null);
    setCurrentItemId('');
    setSelectedPaymentProvider(null);
    setLoadingPaymentMethod(null);
    paymentFlow.resetPayment();
  }, [paymentFlow]);
  
  return {
    // State
    subscriptionPlans,
    tokenPacks,
    loading,
    error,
    isPaymentMethodModalVisible,
    currentPurchaseType,
    currentItemId,
    selectedPaymentProvider,
    loadingPaymentMethod,

    // Payment flow state (from shared hook)
    isPaymentModalVisible: paymentFlow.isPaymentModalVisible,
    paymentUrl: paymentFlow.paymentUrl,
    orderId: paymentFlow.orderId,
    polling: paymentFlow.polling,
    paymentStatus: paymentFlow.paymentStatus,
    showConfetti: paymentFlow.showConfetti,

    // Actions
    loadData,
    initiatePurchase,
    selectPaymentMethod,
    pollPaymentStatus: paymentFlow.pollPaymentStatus,
    resetPayment,
    setShowConfetti: paymentFlow.setShowConfetti,
  };
} 